/* eslint-disable react-hooks/exhaustive-deps, react/forbid-component-props */
import Head from 'next/head';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Tile from '../components/_generic/tile/Tile';
import TileBody from '../components/_generic/tile/TileBody';
import FooterContainer from '../components/footer/FooterContainer';
import FooterLegalText from '../components/footer/FooterLegalText';
import FooterLogin from '../components/footer/FooterLoginBlock';
import HeaderEmpty from '../components/header/HeaderEmpty';
import AccountOptions from '../components/myaccount/AccountOptions';
import VoucherStatus from '../components/myaccount/vouchers/VoucherStatus';
import Wallet from '../components/wallet/Wallet';
import { FAVICON } from '../config/constants/images';
import { ACCOUNT_LAYOUT } from '../config/constants/layout-types';
import { PAGE_TYPES } from '../config/constants/page-types';
import { BRAND_WOWCHER, SITE_WOWCHER } from '../config/setup/setup';
import {
  ACCOUNT_OPTIONS_HI,
  MINI_MENU,
  MINI_MENU_WALLET,
  MINI_MENU_VOUCHER,
  WOWCHER_STATUS_TITLE,
  WOWCHER_STATUS_LIST,
  VIEW_HISTORY,
  VIEW_YOUR_WOWCHERS,
  ACCOUNT_OPTIONS_LOWER,
  RAF_ENABLED_MINI_MENU,
} from '../config/text/text';
import { isWowcherBrand } from '../helpers/pages';
import ThemeContext from '../providers/ThemeProvider';
import { getClientSideLayoutEssentials } from '../redux/actions/clientRender';
import { getWallet } from '../redux/actions/wallet';

const AccountLayout = ({
  children,
  extraClasses,
  pageType = PAGE_TYPES.account,
  showStatus = true,
  showWallet = true,
  title: pageTitle,
}) => {
  const theme = useContext(ThemeContext);
  const user = useSelector((state) => state.user);
  const BRAND = process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER;
  const fav = FAVICON[BRAND];
  const dispatch = useDispatch();
  const Environment = process.env.NEXT_PUBLIC_SITE || SITE_WOWCHER;
  const mobileMenu = MINI_MENU[process.env.NEXT_PUBLIC_SITE];

  // RAF Enabled mobile menu controlled by the env variable
  const rafEnabled = JSON.parse(process.env.NEXT_PUBLIC_RAF_ENABLED === 'true');
  const isWowcher = isWowcherBrand();
  const options =
    rafEnabled && isWowcher
      ? RAF_ENABLED_MINI_MENU[process.env.NEXT_PUBLIC_SITE]
      : mobileMenu;

  const [userprofile, userWallet, isAuthenticated] = useSelector((state) => [
    state.user.userprofile,
    state.wallet.userWallet,
    state.user.isAuthenticated,
  ]);

  useEffect(() => {
    dispatch(getClientSideLayoutEssentials(user, ACCOUNT_LAYOUT, pageType));
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    dispatch(getWallet());
  }, [dispatch]);

  const firstName = userprofile?.firstName || '';
  const merchant = userprofile?.businessOwner;

  if (!isAuthenticated) return null;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <link href={fav} rel="icon" />
        <script defer src="https://pay.google.com/gp/p/js/pay.js" />
      </Head>
      <div className={extraClasses}>
        <HeaderEmpty hasMobileNavigation site={Environment} />
        <div className="account app app--full-height" id="main-view">
          <div className="inner">
            <div className="side">
              <div className="side__item">
                <Tile title={ACCOUNT_OPTIONS_HI(firstName)}>
                  <TileBody>
                    <AccountOptions
                      accountOptionsLower={ACCOUNT_OPTIONS_LOWER}
                      merchant={merchant}
                      options={options}
                      site={Environment}
                    />
                  </TileBody>
                </Tile>
              </div>
              {showWallet && (
                <div className="side__item">
                  <Tile>
                    <TileBody className="account__wallet" nopadding>
                      <Wallet userWallet={userWallet} />
                      <Link href={MINI_MENU_WALLET}>
                        <a
                          className="account__link-wallet"
                          onClick={() => dispatch(getWallet())}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              dispatch(getWallet());
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {VIEW_HISTORY[process.env.NEXT_PUBLIC_SITE]}
                        </a>
                      </Link>
                    </TileBody>
                  </Tile>
                </div>
              )}
              {showStatus && (
                <div className="side__item">
                  <Tile
                    title={WOWCHER_STATUS_TITLE[process.env.NEXT_PUBLIC_SITE]}
                  >
                    <TileBody>
                      <VoucherStatus
                        list={
                          WOWCHER_STATUS_LIST[process.env.NEXT_PUBLIC_BRAND]
                        }
                        site={Environment}
                      />
                      <Link href={MINI_MENU_VOUCHER}>
                        <a className="account__link-status">
                          {VIEW_YOUR_WOWCHERS[process.env.NEXT_PUBLIC_SITE]}
                        </a>
                      </Link>
                    </TileBody>
                  </Tile>
                </div>
              )}
            </div>
            <div className="main">{children}</div>
          </div>
        </div>
        {pageType === PAGE_TYPES.account && <FooterLogin />}
        {pageType === PAGE_TYPES.vouchers && (
          <FooterContainer>
            <FooterLegalText
              isCheckout
              isSecurePage
              isVouchers
              site={Environment}
            />
          </FooterContainer>
        )}
      </div>
      <style jsx>{`
        .account {
          max-width: 1024px;
          margin: 0 auto;
          padding: 30px 0;
          width: 100%;
        }
        .inner {
          display: flex;
          margin: 0 -15px;
        }
        .side {
          width: 33.33%;
        }
        .side,
        .main {
          padding: 0 15px;
        }
        .side__item {
          margin-bottom: 3rem;
        }
        .main {
          width: 66.67%;
        }
        .account :global(.account__wallet .tile-main) {
          padding: 0;
        }
        .account__link-wallet,
        .account__link-status {
          color: ${theme.colors.primary};
          text-decoration: none;
          text-align: right;
          display: block;
        }
        .account__link-wallet:hover,
        .account__link-status:hover {
          color: ${theme.colors.linkhover};
        }
        .account__link-wallet {
          padding-top: 15px;
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .inner {
            display: block;
            margin: 0;
          }
          .account {
            padding: 0;
          }
          .side {
            display: none;
          }
          .main {
            padding: 0;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

AccountLayout.propTypes = {
  children: PropTypes.array,
  extraClasses: PropTypes.string,
  pageType: PropTypes.string,
  showStatus: PropTypes.bool,
  showWallet: PropTypes.bool,
  title: PropTypes.string,
};

export default AccountLayout;
